import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil, Observable, zip } from 'rxjs';
import { GUEST_CART_ID, TOKEN } from '../../constants/constant';
import { environment } from '../../environments/environment';
import { Cart, CartItem, CartTotal } from '../../model/cart.model';
import { Page } from '../../model/page.model';
import { GuestService } from '../../service/guest.service';
import { StateService } from '../../service/state.service';
import { StorageService } from '../../service/storage.service';
import { UserService } from '../../service/user.service';

@Component({
  selector: 'app-cart',
  imports: [CommonModule,RouterLink],
  templateUrl: './cart.component.html',
  styleUrl: './cart.component.scss'
})
export class CartComponent {
  @Input() isCartOpen: boolean = false;
  unsubscribe$ = new Subject();
  guestCartId: string | null;
  token: string | null;
  cart: Cart[];
  imageUrl = environment.imageUrl;
  cartTotal: CartTotal;
  @Output()
  cartCount = new EventEmitter<number>();

  constructor(private stateService: StateService,
    private guestService: GuestService,
    private localService: StorageService,
    private router: Router,
    private userService: UserService,
    private toastr: ToastrService,
    private activatedRoute: ActivatedRoute,) { }

  ngOnInit(): void {
    this.guestCartId = this.localService.getItem(GUEST_CART_ID);
    this.token = this.localService.getItem(TOKEN);

    this.stateService.cartSideBar$.pipe(takeUntil(this.unsubscribe$))
      .subscribe(res => {
        this.isCartOpen = res;
      })

    this.stateService.cartUpdated$.pipe(takeUntil(this.unsubscribe$))
      .subscribe(res => {
        this.fetchCartDetails();
      })

    this.stateService.guestUserFetch$.pipe(takeUntil(this.unsubscribe$))
      .subscribe(res => {
        if (res) {
          this.fetchCartDetails();
        }
      })

    this.stateService.tokenFetch$.pipe(takeUntil(this.unsubscribe$))
      .subscribe(res => {
        if (res) {
          this.fetchCartDetails();
        }
      })

    this.stateService.cartTotal$.pipe(takeUntil(this.unsubscribe$))
      .subscribe(res => {
        this.cartTotal = res;
      })
  }

  toggleCart() {
    this.stateService.showCartSideBar(false)
  }

  fetchCartDetails() {
    let page: Page = { pageNo: 1, pageSize: 50 }
    let cartArr: Observable<any>[] = [];
    this.guestCartId = this.localService.getItem(GUEST_CART_ID);
    this.token = this.localService.getItem(TOKEN);

    if (this.token) {
      cartArr = [this.userService.getUserCart(page),
      this.userService.getUserCartCalculation()
      ]
    } else if (this.guestCartId) {
      cartArr = [this.guestService.getGuestCart(page, this.guestCartId),
      this.guestService.getGuestCartCalculation(this.guestCartId)
      ]
    }

    zip(cartArr)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(res => {
        this.cart = res[0]?.data?.list;
        this.cartTotal = res[1]?.data[0]?.DiscountPriceTotal;
        this.cartCount.next(this.cart?.length);
        this.stateService.sendCartDetails(this.cart);
        this.stateService.sendCartTotal(res[1]?.data[0]);
      })
  }

  getUserCartCalculation() {
    if (this.localService.getItem(TOKEN))
      this.userService.getUserCartCalculation().subscribe((res: any) => {
        this.stateService.sendCartTotal(res?.data[0]);
      })
    else
      this.guestService.getGuestCartCalculation(this.guestCartId).subscribe((res: any) => {
        this.stateService.sendCartTotal(res?.data[0]);
      })
  }

  hideCart() {
    this.stateService.showCartSideBar(false);
  }

  shopNow() {
    this.stateService.showCartSideBar(false);
    this.router.navigate(['shop']);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  checkout() {
    if (this.cart.length > 0 && !this.localService.getItem(TOKEN)) {
      this.stateService.showCartSideBar(false);
      this.checkOpenSignIn()
    } else {
      this.router.navigate(['/account/checkout']);
      this.stateService.showCartSideBar(false);
    }
  }

  checkOpenSignIn() {
    const queryParams: Params = { signIn: 1, checkout: 1 };

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });

  }

  private debounceTimer: any = null;

  changeQuantity(product: Cart, isDecreased: boolean) {
    if (isDecreased) {
      product.quantity -= 1;
    } else {
      product.quantity += 1;
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      const cart: CartItem = {
        skuId: product.skuId.toString(),
        quantity: product.quantity,
        flag: 1
      };

      if (product.quantity === 0) {
        this.deleteProduct(product);
      } else {
        this.updateProduct(cart);
      }
    }, 250);
  }

  updateProduct(cart: CartItem) {
    if (this.token) {
      this.userService.addProductUserCart(cart).pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: res => {
            // this.toastr.info('Product Updated Successfully');
            this.stateService.sendCartUpdated(true);
          },
          error: (err) => {
            this.toastr.error("Something Went Wrong")
          }
        });
    } else {
      this.guestService.addProductGuestCart(cart, this.guestCartId).pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: res => {
            // this.toastr.info('Product Updated Successfully');
            this.stateService.sendCartUpdated(true);
          },
          error: (err) => {
            this.toastr.error("Something Went Wrong")
          }
        });
    }
  }

  deleteProduct(product: Cart) {
    let cart: CartItem = {
      skuId: product.skuId.toString()
    }
    if (this.localService.getItem(TOKEN)) {
      this.userService.deleteProductUserCart(cart).pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: res => {
            // this.toastr.info('Product Deleted Successfully');
            this.cart = this.cart.filter((cartItem) => cartItem != product)
            this.stateService.sendCartDetails(this.cart)
            this.stateService.sendCartUpdated(true);
          },
          error: (err) => {
            this.toastr.error("Something Went Wrong")
          }
        });
    } else {
      this.guestService.deleteProductGuestCart(cart, this.guestCartId).pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: res => {
            this.toastr.info('Product Deleted Successfully');
            this.stateService.sendCartUpdated(true);
          },
          error: (err) => {
            this.toastr.error("Something Went Wrong")
          }
        });
    }
  }

  identify(index: number, item: Cart) {
    return item ? item.productId : undefined;
  }
}
